import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Filter } from 'lucide-react';
import { subYears, subMonths, subDays, format } from 'date-fns';
import clsx from 'clsx';

// Quick-fill presets, each resolving to a date relative to "now" (today for "now").
const DATE_PRESETS: { label: string; getDate: () => Date }[] = [
  { label: '5y', getDate: () => subYears(new Date(), 5) },
  { label: '1y', getDate: () => subYears(new Date(), 1) },
  { label: '6m', getDate: () => subMonths(new Date(), 6) },
  { label: '3m', getDate: () => subMonths(new Date(), 3) },
  { label: '1m', getDate: () => subMonths(new Date(), 1) },
  { label: '15d', getDate: () => subDays(new Date(), 15) },
  { label: 'now', getDate: () => new Date() },
];

function DatePresetBadges({ onSelect }: { onSelect: (value: string) => void }) {
  return (
    <div className="flex gap-0.5 mt-1.5">
      {DATE_PRESETS.map((preset) => (
        <button
          key={preset.label}
          type="button"
          onClick={() => onSelect(format(preset.getDate(), 'yyyy-MM-dd'))}
          className="flex-1 px-0.5 py-0.5 text-[10px] text-center rounded-sm bg-dark-surface border border-dark-border text-dark-text-muted hover:text-dark-text-primary hover:border-primary-500 transition-colors"
        >
          {preset.label}
        </button>
      ))}
    </div>
  );
}

interface DateRangeFilterProps {
  fromDate: string | undefined;
  toDate: string | undefined;
  onFromChange: (value: string | undefined) => void;
  onToChange: (value: string | undefined) => void;
  onClear: () => void;
  className?: string;
  /**
   * Opt-in checkbox for rows carrying no date at all. Omit `onNeverChange` and the whole
   * section stays out of the dropdown, which is how callers with no "empty" case behave.
   * It unions with the range: ticked alongside a From/To, the filter matches either.
   */
  neverChecked?: boolean;
  onNeverChange?: (checked: boolean) => void;
  neverLabel?: string;
  /**
   * Opt-in shortcut toggles shown above From/To (e.g. Upcoming / Past). Omit them and the
   * section stays out of the dropdown. Each one writes a whole range, so the From/To inputs
   * below keep showing what it resolved to and stay editable from there.
   */
  modes?: { label: string; active: boolean; onSelect: () => void }[];
}

export function DateRangeFilter({
  fromDate,
  toDate,
  onFromChange,
  onToChange,
  onClear,
  className,
  neverChecked = false,
  onNeverChange,
  neverLabel = 'Never',
  modes,
}: DateRangeFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [isPositioned, setIsPositioned] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const hasActiveFilter = !!fromDate || !!toDate || neverChecked;

  const updatePosition = useCallback(() => {
    if (!buttonRef.current || !dropdownRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownWidth = 240;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let left = rect.left;
    if (left + dropdownWidth > viewportWidth - 8) {
      left = Math.max(8, rect.right - dropdownWidth);
    }
    const top = rect.bottom + 4;
    const maxH = viewportHeight - top - 8;
    setDropdownPosition({ top, left });
    dropdownRef.current.style.maxHeight = `${Math.max(maxH, 120)}px`;
    setIsPositioned(true);
  }, []);

  // Position dropdown when opened
  useEffect(() => {
    if (isOpen) {
      setIsPositioned(false);
      requestAnimationFrame(updatePosition);
    }
  }, [isOpen, updatePosition]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleClear = () => {
    onClear();
  };

  return (
    <div className={clsx('relative inline-flex', className)}>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={clsx(
          'p-0.5 rounded-sm transition-colors',
          hasActiveFilter
            ? 'text-primary-400 hover:text-primary-300'
            : 'text-dark-text-muted hover:text-dark-text-secondary opacity-0 group-hover:opacity-100'
        )}
        title="Filter by date range"
      >
        <Filter className="w-3.5 h-3.5" />
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className={clsx(
            'fixed z-9999 bg-dark-elevated border border-dark-border rounded-lg shadow-xl w-[240px] flex flex-col overflow-hidden',
            isPositioned ? 'animate-scaleIn' : 'opacity-0 pointer-events-none'
          )}
          style={isPositioned
            ? { top: dropdownPosition.top, left: dropdownPosition.left }
            : { top: -9999, left: -9999 }
          }
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-3 space-y-3">
            {modes && modes.length > 0 && (
              <div className="flex gap-1">
                {modes.map((mode) => (
                  <button
                    key={mode.label}
                    type="button"
                    onClick={mode.onSelect}
                    className={clsx(
                      'flex-1 px-2 py-1 text-xs rounded-sm border transition-colors',
                      mode.active
                        ? 'bg-primary-500/15 border-primary-500 text-primary-300'
                        : 'bg-dark-surface border-dark-border text-dark-text-muted hover:text-dark-text-primary hover:border-primary-500'
                    )}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            )}
            <div>
              <label className="block text-xs text-dark-text-muted mb-1">From</label>
              <input
                type="date"
                value={fromDate || ''}
                onChange={(e) => onFromChange(e.target.value || undefined)}
                className="w-full px-2 py-1.5 text-sm bg-dark-surface border border-dark-border rounded-sm text-dark-text-primary focus:outline-hidden focus:border-primary-500"
              />
              <DatePresetBadges onSelect={onFromChange} />
            </div>
            <div>
              <label className="block text-xs text-dark-text-muted mb-1">To</label>
              <input
                type="date"
                value={toDate || ''}
                onChange={(e) => onToChange(e.target.value || undefined)}
                className="w-full px-2 py-1.5 text-sm bg-dark-surface border border-dark-border rounded-sm text-dark-text-primary focus:outline-hidden focus:border-primary-500"
              />
              <DatePresetBadges onSelect={onToChange} />
            </div>
            {onNeverChange && (
              <label className="flex items-start gap-2 cursor-pointer select-none pt-1 border-t border-dark-border">
                <input
                  type="checkbox"
                  checked={neverChecked}
                  onChange={(e) => onNeverChange(e.target.checked)}
                  className="mt-0.5 w-3.5 h-3.5 accent-primary-500 cursor-pointer"
                />
                <span className="text-xs text-dark-text-secondary leading-tight">
                  {neverLabel}
                  {(fromDate || toDate) && (
                    <span className="block text-[10px] text-dark-text-muted">
                      Kept alongside the range above, not filtered by it
                    </span>
                  )}
                </span>
              </label>
            )}
          </div>

          <div className="flex items-center justify-between px-3 py-2 border-t border-dark-border">
            {hasActiveFilter ? (
              <button
                onClick={handleClear}
                className="text-xs text-dark-text-secondary hover:text-dark-text-primary transition-colors"
              >
                Clear
              </button>
            ) : (
              <span />
            )}
            <button
              onClick={() => setIsOpen(false)}
              className="px-3 py-1 text-xs text-dark-text-secondary hover:text-dark-text-primary transition-colors"
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
